/* eslint-disable react-hooks/exhaustive-deps */
import { CURRICULUM_SLUGS } from '@/constants'
import { STYLES } from '@/constants/styles'
import { TREE_OPTIONS_DOWNLOAD_CUSTOM_VIEW } from '@/constants/treepickeroptions'
import useDisabledLearningAreasAndStages from '@/hooks/useDisabledLearningAreasAndStages'
import { useSideBySideSupport } from '@/hooks/useSideBySideSupport'
import { useToggle } from '@/hooks/useToggle'
import { Syllabus, WpHomepage } from '@/kontent/content-types'
import { TaxoKeyLearningArea } from '@/kontent/taxonomies'
import { SYLLABUS_TABS } from '@/legacy-ported/constants'
import { ISyllabusTab } from '@/legacy-ported/types'
import { customSyllabusQueryString } from '@/legacy-ported/utilities/functions'
import { getTitleWithSuffix } from '@/pages/_app'
import {
	CustomSyllabusTab,
	IPropWithClassNameChildren,
	Mapping,
	SyllabusTab,
	TaxoStageWithLifeSkill,
} from '@/types'
import { FocusareaOrOptionListOrFocusareoptionExtended } from '@/types/customKontentTypes'
import {
	getSeoDescriptionFieldKeyByTab,
	getTagFromYears,
	getTaxoCodenames,
	isIntersect,
	isRichtextElementEmpty,
} from '@/utils'
import { isCecSyllabus, shouldHideTabBasedOnCecContentHide } from '@/utils/cec'
import { getSyllabusUrlFromMappingBySyllabusCodename } from '@/utils/getSyllabusUrlFromMapping'
import { getUrlFromSlugs } from '@/utils/getUrlFromMapping'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import { isFocusarea } from '@/utils/type_predicates'
import {
	ElementModels,
	IContentItemsContainer,
	Responses,
} from '@kontent-ai/delivery-sdk'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Head from 'next/head'
import NextLink from 'next/link'
import { NextRouter, useRouter } from 'next/router'
import { Button } from 'nsw-ds-react'
import { forwardRef, useMemo } from 'react'
import Icon from './Icon'
import Link from './Link'
import RichText from './RichText'
import { SyllabusHeader } from './SyllabusHeader'
import SyllabusNotification from './SyllabusNotification'
import { DownloadSyllabusOrCourseButton } from './cec/DownloadSyllabusOrCourseButton'
import { useKontentHomeConfig } from './contexts/KontentHomeConfigProvider'
import { useWebsiteConfiguration } from './contexts/WebsiteConfigurationProvider'
import { DownloadViewModal } from './modals/DownloadViewModal'
import {
	EditViewModal,
	ViewSelection,
	useEditViewModal,
} from './modals/EditViewModal'

export interface SyllabusViewNewProps extends IPropWithClassNameChildren {
	config: Responses.IViewContentItemResponse<WpHomepage>
	mappings: Mapping[]
	linkedItems: IContentItemsContainer
	syllabus: Syllabus
	allSyllabuses: Responses.IListContentItemsResponse<Syllabus>
	allKeyLearningAreas: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[]
	allStages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[]
	currentTab?: SyllabusTab
	preview?: boolean
	defaultFocusAreaUrls?: Record<string, string>
}

const addDotAtTheEnd = (text: string) => {
	if (!text) return text

	let result = text.trim()
	if (result[result.length - 1] !== '.') {
		result += '.'
	}
	return result
}

export const useMetaDescriptionSyllabus = ({
	syllabus,
	syllabusTab,
	stage,
	focusAreaOrOptionlist,
}: {
	syllabus: Syllabus
	syllabusTab: ISyllabusTab
	stage?: string
	focusAreaOrOptionlist?: FocusareaOrOptionListOrFocusareoptionExtended
}) => {
	return useMemo(() => {
		let seoDescription =
			syllabus.elements[getSeoDescriptionFieldKeyByTab(syllabusTab.id)]
				?.value
		if (
			syllabusTab.id === 'content' &&
			stage &&
			focusAreaOrOptionlist &&
			isFocusarea(focusAreaOrOptionlist) &&
			focusAreaOrOptionlist.elements.seo_description.value
		) {
			if (focusAreaOrOptionlist.elements.seo_description.value) {
				return addDotAtTheEnd(
					focusAreaOrOptionlist.elements.seo_description.value,
				)
			}
		}

		// if there's SEO Description field speicifed, use it
		if (seoDescription) {
			seoDescription = addDotAtTheEnd(seoDescription)
			if (
				syllabusTab.id === 'content' &&
				stage &&
				focusAreaOrOptionlist
			) {
				return `${seoDescription} ${stage} - ${focusAreaOrOptionlist.elements.title.value}.`
			}
			return seoDescription
		}
	}, [
		syllabus.elements.title?.value,
		syllabusTab.name,
		syllabus.elements.seo_description_aim?.value,
		syllabus.elements.seo_description_assessment?.value,
		syllabus.elements.seo_description_content?.value,
		syllabus.elements.seo_description_glossary?.value,
		syllabus.elements.seo_description_outcomes?.value,
		syllabus.elements.seo_description_overview?.value,
		syllabus.elements.seo_description_rationale?.value,
		syllabus.elements.seo_description_support?.value,
		focusAreaOrOptionlist,
		stage,
	])
}

const TabLink = forwardRef<HTMLAnchorElement, any>((props, ref) => {
	return <Link ref={ref} {...props} scroll={false} />
})

const TabLinkNoPrefetch = forwardRef<HTMLAnchorElement, any>((props, ref) => {
	return <Link ref={ref} {...props} scroll={false} prefetch={false} />
})

export const SyllabusViewNew = (props: SyllabusViewNewProps) => {
	const {
		children,
		syllabus,
		config,
		linkedItems,
		mappings,
		allSyllabuses,
		defaultFocusAreaUrls,
		allKeyLearningAreas,
		allStages,
		preview = false,
		currentTab = SYLLABUS_TABS[0].id,
	} = props
	const router = useRouter()
	const { route, query } = router
	let {
		learningarea: paramLearningArea,
		syllabus: paramSyllabus,
		tab: paramTab,
		stage: paramStage,
		focusarea: paramFocusarea,
	} = (router?.['components']?.[route] as NextRouter)?.query || query

	// If the route is /learning-areas/[learningarea]/[syllabus]/assessment/[section],
	// then the tab is assessment
	if (
		route ===
		'/learning-areas/[learningarea]/[syllabus]/assessment/[section]'
	) {
		paramTab = 'assessment'
	}

	let _currentTab
	const { isInIframe } = useKontentHomeConfig()
	const wcConfig = useWebsiteConfiguration()
	const isViewSideBySideSupported = useSideBySideSupport()

	// Computed
	const selectedSyllabuses = useMemo(
		() => [syllabus.system.codename],
		[syllabus.system.codename],
	)

	const { disabledStages, disabledLearningAreas } =
		useDisabledLearningAreasAndStages(config)

	// Edit view
	const { displayEditViewModal, toggleEditOverlay, handleCancel } =
		useEditViewModal(false)

	// Download View
	const [showDownloadOverlay, toggleDownloadOverlay] = useToggle()

	const syllabusTab: ISyllabusTab = SYLLABUS_TABS.find((tab) => {
		if (paramTab === 'overview' || !paramTab)
			return tab.id === 'course-overview'
		return tab.id === paramTab
	})
	if (syllabusTab) {
		let queryTab =
			paramTab === 'overview' || !paramTab ? 'course-overview' : paramTab
		_currentTab = queryTab || (currentTab as CustomSyllabusTab)
	}

	const taxoStagesCodenamesOfSyllabus = useMemo<
		TaxoStageWithLifeSkill[]
	>(() => {
		const tmp = getTaxoCodenames(syllabus.elements.stages__stages).filter(
			(taxoStage) => !disabledStages.includes(taxoStage),
		) as TaxoStageWithLifeSkill[]
		if (isIntersect(['stage_4', 'stage_5', 'stage_6'], tmp)) {
			tmp.push('life_skills')
		}
		return tmp
	}, [disabledStages, syllabus.elements.stages__stages])

	const taxoStagesCodenamesOfSyllabusForDownload = useMemo<
		TaxoStageWithLifeSkill[]
	>(() => {
		const tmp = getTaxoCodenames(syllabus.elements.stages__stages).filter(
			(taxoStage) => !disabledStages.includes(taxoStage),
		) as TaxoStageWithLifeSkill[]
		if (isIntersect(['stage_4', 'stage_5'], tmp)) {
			tmp.push('life_skills')
		}
		return tmp
	}, [disabledStages, syllabus.elements.stages__stages])

	// For download modal, disable all stages except the ones that belong to this syllabus
	const disabledStagesForDownload = useMemo(() => {
		const allStageCodenames = allStages.map((stage) => stage.codename)
		const disabled = allStageCodenames.filter(
			(stageCodename) =>
				!taxoStagesCodenamesOfSyllabus.includes(
					stageCodename as TaxoStageWithLifeSkill,
				),
		) as TaxoStageWithLifeSkill[]

		// Disable Life Skills for Year 11-12 only syllabuses
		// If syllabus only has stage_6 (and no stage_4 or stage_5), disable life_skills
		const syllabusStages = getTaxoCodenames(
			syllabus.elements.stages__stages,
		)
		const hasOnlyStage6 =
			syllabusStages.includes('stage_6') &&
			!syllabusStages.includes('stage_4') &&
			!syllabusStages.includes('stage_5')

		if (hasOnlyStage6 && !disabled.includes('life_skills')) {
			disabled.push('life_skills')
		}

		return disabled
	}, [
		allStages,
		taxoStagesCodenamesOfSyllabus,
		syllabus.elements.stages__stages,
	])

	const canonicalUrl = useMemo(() => {
		const syllabusPath = getSyllabusUrlFromMappingBySyllabusCodename(
			mappings,
			syllabus.system.codename,
			true,
			true,
		)
		const otherParams = []
		if (paramTab) {
			otherParams.push(`${paramTab}`)
		}
		if (paramStage) {
			otherParams.push(`${paramStage}`)
		}
		if (paramFocusarea) {
			// focus area canonical is set in the [focusarea]/index.tsx
			return ''
			// otherParams.push(`${paramFocusarea}`)
		}
		const otherSlugs = otherParams.join('/')
		return syllabusPath + (otherSlugs ? `/${otherSlugs}` : '')
	}, [mappings, syllabus.system.codename])

	const metaDescription = useMetaDescriptionSyllabus({
		syllabus,
		syllabusTab,
	})

	// OC2-6052: Server-side (`getDefaultFocusAreaUrls`) sets `__content_default__`
	// when the syllabus has exactly one course with `display_mode = Yes` mapped to
	// exactly one accordion. That's the same shape the CMS `_content_default`
	// redirect rule fires on — linking straight to the final URL avoids a 308 that
	// races with Next.js 12's client-side router on tab click.
	const contentDefaultHref =
		defaultFocusAreaUrls?.['__content_default__'] ?? null

	const recordOfChangesUrl = `/resources/record-of-changes?syllabus=${syllabus.elements.syllabus.value[0]?.codename}`
	const _isCecSyllabus = isCecSyllabus(syllabus)

	const syllabusesListForModals = allSyllabuses.items.filter(
		(s) => !isCecSyllabus(s),
	)

	const syllabusWithPublicationYear = useMemo(() => {
		return getSyllabusTitleWithPublicationYear(syllabus)
	}, [syllabus])

	const shouldShowSyllabusImplementationInfo = useMemo(() => {
		return (
			!shouldHideTabBasedOnCecContentHide(
				syllabus,
				'implementation_info',
			) &&
			syllabus.elements.implementation_title.value &&
			!isRichtextElementEmpty(syllabus.elements.implementation_info) &&
			!isInIframe
		)
	}, [
		syllabus.elements.implementation_title.value,
		syllabus.elements.implementation_info,
		isInIframe,
	])

	// Events
	const handleEditViewModalConfirm = (selectedItems: ViewSelection) => {
		const tabIds = selectedItems.tabs

		router.push({
			pathname: getUrlFromSlugs(CURRICULUM_SLUGS.CUSTOM_SYLLABUSES),
			search: customSyllabusQueryString({
				stageIds: selectedItems.stages,
				tabIds,
				tagIds: selectedItems.tags,
				syllabusIds: selectedItems.syllabuses,
			}),
		})
	}

	return (
		<>
			<Head>
				<title>
					{getTitleWithSuffix(
						`${syllabusWithPublicationYear}${
							syllabusTab ? ` - ${syllabusTab.name}` : ''
						}`,
						wcConfig,
					)}
				</title>
				{canonicalUrl && (
					<link key="canonical" rel="canonical" href={canonicalUrl} />
				)}
				<meta name="description" content={metaDescription} />
			</Head>
			<SyllabusHeader
				className="[&_.SyllabusHeader\_\_buttons>*]:flex-shrink-0 [&_.SyllabusHeader\_\_buttons]:flex-wrap"
				pretitle={getTagFromYears(
					syllabus.elements.stages__stage_years.value,
				)}
				title={syllabusWithPublicationYear}
				onEditViewClick={
					isInIframe || _isCecSyllabus ? undefined : toggleEditOverlay
				}
				onDownloadViewClick={
					isInIframe || _isCecSyllabus
						? undefined
						: toggleDownloadOverlay
				}
				slotAfterButtons={
					!isInIframe && (
						<>
							{_isCecSyllabus && (
								<DownloadSyllabusOrCourseButton
									syllabusCodename={syllabus.system.codename}
									syllabusTitle={syllabusWithPublicationYear}
									isPreviewMode={preview}
								/>
							)}
							<NextLink href={recordOfChangesUrl} passHref>
								<Button
									link={recordOfChangesUrl}
									className="flex justify-center"
									style="white"
									css={{
										'& > *': {
											flexShrink: 0,
										},
									}}
									linkComponent="a"
								>
									<span className="mr-2">
										Record of changes
									</span>
									<Icon icon="mdi:arrow-right" />
								</Button>
							</NextLink>
						</>
					)
				}
			/>
			{shouldShowSyllabusImplementationInfo && (
				<SyllabusNotification
					title={syllabus.elements.implementation_title.value}
					summary={syllabus.elements.implementation_summary.value}
				>
					<RichText
						richTextElement={syllabus.elements.implementation_info}
						linkedItems={linkedItems}
						mappings={mappings}
						css={STYLES.DARK_BACKGROUND_RTE}
					/>
				</SyllabusNotification>
			)}

			<div className="nsw-container px-0 lg:px-4 lg:pt-8">
				<Tabs
					className="w-full bg-nsw-off-white lg:bg-white syllabus-header__tab-bar"
					value={_currentTab}
					indicatorColor="secondary"
					variant="scrollable"
					scrollButtons="auto"
					allowScrollButtonsMobile
					sx={{
						'.MuiTabs-scroller:before': {
							content: '""',
							display: 'block',
							height: 2,
							background: 'var(--nsw-grey-04)',
							position: 'absolute',
							width: '100%',
							left: 0,
							bottom: 0,
						},
						'.MuiTab-root:focus': {
							outlineOffset: '-3px',
						},
					}}
				>
					{SYLLABUS_TABS.filter((tab) => {
						// if it's a cec syllabus, hide the tabs based on the cec_content_hide field
						return !shouldHideTabBasedOnCecContentHide(
							syllabus,
							tab.id,
						)
					}).map((tab) => {
						const tabSlug =
							tab.id === 'course-overview' ? 'overview' : tab.id

						const tabHref =
							tab.id === 'content' && contentDefaultHref
								? contentDefaultHref
								: `/learning-areas/${paramLearningArea}/${paramSyllabus}/${tabSlug}`

						return (
							<Tab
								key={tab.id}
								href={tabHref}
								label={
									tab.id === 'course-overview'
										? 'Overview'
										: tab.name
								}
								value={tab.id}
								LinkComponent={
									preview ? TabLinkNoPrefetch : TabLink
								}
							></Tab>
						)
					})}
				</Tabs>
				{children}
			</div>

			{displayEditViewModal && (
				<EditViewModal
					modalStatus={displayEditViewModal}
					onConfirm={handleEditViewModalConfirm}
					onCancel={handleCancel}
					selectedElements={SYLLABUS_TABS.map((tab) => tab.id)}
					selectedSyllabuses={selectedSyllabuses}
					selectedStages={taxoStagesCodenamesOfSyllabus}
					syllabuses={syllabusesListForModals}
					keyLearningAreas={allKeyLearningAreas}
					stages={allStages}
					disabledStages={disabledStages}
					disabledLearningAreas={disabledLearningAreas}
				/>
			)}

			{showDownloadOverlay && (
				<DownloadViewModal
					modalStatus={showDownloadOverlay}
					onCancel={toggleDownloadOverlay}
					selectedElements={
						TREE_OPTIONS_DOWNLOAD_CUSTOM_VIEW.flatMap((option) => {
							// Elements that should not be preselected
							const excludedIds = [
								'examples',
								'tags',
								'teaching-advice',
								'assessment',
								'glossary',
							]

							// if (
							// 	option.id === 'content' ||
							// 	option.id === 'all-content'
							// ) {
							// 	return option.children
							// 		? option.children
							// 			.filter(
							// 				(child) =>
							// 					!excludedIds.includes(
							// 						child.id,
							// 					),
							// 			)
							// 			.map((child) => child.id)
							// 		: []
							// }

							// Skip excluded top-level elements
							if (excludedIds.includes(option.id)) {
								return []
							}

							return option.children
								? option.children
										.filter(
											(child) =>
												!excludedIds.includes(child.id),
										)
										.map((child) => child.id)
								: [option.id]
						}) as CustomSyllabusTab[]
					}
					selectedSyllabuses={selectedSyllabuses}
					selectedStages={taxoStagesCodenamesOfSyllabusForDownload}
					syllabuses={syllabusesListForModals}
					keyLearningAreas={allKeyLearningAreas}
					stages={allStages}
					disabledStages={disabledStagesForDownload}
					disabledLearningAreas={disabledLearningAreas}
					isPreviewMode={preview}
					hideLearningAreasColumn={true}
					useNestedStageGroups={true}
					showElementsSelectAll={true}
					hideElementsExpandCollapse={true}
					showAllExpandables={true}
					customLifeSkillsLabel="Life Skills (7–10)"
					hideLifeSkillsDivider={true}
					hideAllDividers={true}
					pageType={'Syllabus overview'}
				/>
			)}
			{!isInIframe && isViewSideBySideSupported && (
				<Button
					style="dark-outline"
					className="fixed bottom-5 z-[201] right-[88px] bg-white gap-2 hidden lg:inline-flex"
					css={{
						'html:has(&) .nav-page__scrollToTop': {
							marginBottom: '1rem',
							right: '1rem',
						},
					}}
					onClick={() => {
						const url = new URL(window.location.href)
						const pathnameWithSearch = encodeURIComponent(
							url.href.replace(url.origin, ''),
						)
						window.location.href = `/view-side-by-side?l=${pathnameWithSearch}&r=/find-syllabus`
					}}
				>
					<span>View side-by-side</span>
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H9V6H4V18H9V20H4ZM11 22V2H13V4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H13V22H11ZM13 18H20V6H13V18Z"
							fill="currentColor"
						/>
					</svg>
				</Button>
			)}
		</>
	)
}

export default SyllabusViewNew
